import Konva from 'konva';
import _ from 'lodash';
import React, { useState } from 'react';
import { KonvaNodeEvents, Rect } from 'react-konva';
import { useSetRecoilState } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  getBestContrastColor,
  getQualitativeColor,
} from '../../common/color.helper';
import { ActualityJob } from '../../feed/store/actuality.types';
import { feedHoveredPickListId } from '../../feed/store/feed.state';
import { ticksInPixel } from '../viewer/layers/SchedulerLayer';

const colors = TwTheme.extend.colors;

export const jobFeatureHeight = 34;
export const jobPadding = 8;
export const actualJobHeight = 20;
export const analyzedJobHeight = actualJobHeight / 10;

export type ActualityEventsGroupFeatureProps = {
  job: ActualityJob;
  offsetY: number;
  isFirst?: boolean;
  isLast?: boolean;
  isActive?: boolean;
  viewJobMode?: string;
};

// 🎨 Patterns are now handled by dedicated utility: ../../utils/konvaPatterns.ts
// This provides native Konva.Path-based patterns for optimal performance

const ActualityEventsGroupFeature: React.FC<
  ActualityEventsGroupFeatureProps
> = props => {
  const { job, offsetY = 0 } = props;
  const setHoveredGlobal = useSetRecoilState(feedHoveredPickListId);
  const setSelectedJobId = null; // useSetRecoilState(feedSelectedPickListId);
  const setSelectedAgentId = null; // useSetRecoilState(feedSelectedAgentId);
  const [hovered, setHovered] = useState(false);

  const id = job.jobId;
  const actualJobStart = job.minEventTime.getTime() / ticksInPixel;
  const actualJobEnd = job.maxEventTime.getTime() / ticksInPixel;
  const actualJobLength = Math.max(actualJobEnd - actualJobStart, 1);
  const idleLength = job.totalIdleDurationBetweenJobs / ticksInPixel;

  const actualJobFill = getQualitativeColor(job.agentId, 'dimension')?.[0];

  const analyzedJobWidthRaw =
    ((job.analyzedDuration ?? 0) * 1000) / ticksInPixel;
  const analyzedJobDuration = Math.abs(actualJobLength - analyzedJobWidthRaw);
  const analyzedJobStart = Math.min(
    actualJobEnd,
    actualJobStart + analyzedJobWidthRaw,
  );

  const isUnderperform = analyzedJobWidthRaw < actualJobLength;

  // 🎨 Create seamless diagonal hatch pattern using SVG (like EventFeature icons)
  const diagonalPattern = React.useMemo(() => {
    if (idleLength <= 0) return null;

    const patternSize = 4; // Larger pattern for better quality
    const lineWidth = 0.5; // Width of diagonal lines

    // Create SVG pattern using the same approach as EventFeature icons
    const svgPattern = `
      <svg width="${patternSize}" height="${patternSize}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="diagonalStripes" patternUnits="userSpaceOnUse" width="${patternSize}" height="${patternSize}">
            <rect width="${patternSize}" height="${patternSize}" fill="${colors.timeline.idle}"/>
            <g stroke="${colors.timeline.idle}" stroke-width="${lineWidth}" stroke-linecap="round" opacity="${hovered ? 0.6 : 0.8}">
              <!-- Main diagonal line -->
              <line x1="-${lineWidth}" y1="${patternSize + lineWidth}" x2="${patternSize + lineWidth}" y2="-${lineWidth}"/>
              <!-- Additional lines for seamless tiling -->
              <line x1="-${lineWidth + patternSize}" y1="${patternSize + lineWidth}" x2="${lineWidth}" y2="-${lineWidth}"/>
              <line x1="${patternSize - lineWidth}" y1="${patternSize + lineWidth}" x2="${patternSize * 20 + lineWidth}" y2="-${lineWidth}"/>
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#diagonalStripes)"/>
      </svg>
    `;

    // Convert SVG to data URL using the same approach as EventFeature
    const dataUrl = `data:image/svg+xml;base64,${btoa(svgPattern)}`;
    const img = new window.Image();
    img.src = dataUrl;

    return new Promise<HTMLImageElement>(resolve => {
      img.onload = () => resolve(img);
      img.onerror = error => {
        console.error('Failed to load pattern image:', error);
      };
    });
  }, [idleLength, hovered]);

  // State for pattern image
  const [patternImage, setPatternImage] =
    React.useState<HTMLImageElement | null>(null);

  React.useEffect(() => {
    if (diagonalPattern) {
      diagonalPattern.then(setPatternImage);
    }
  }, [diagonalPattern]);

  const analyzedJobFill = _.isNil(job.analyzedDuration)
    ? colors.timeline.unknown
    : isUnderperform
      ? colors.timeline.underperform
      : colors.timeline.overperform;

  function onClick() {
    setSelectedAgentId(job.agentId);
    setSelectedJobId(current => (current === job.jobId ? null : job.jobId));
  }

  // Set corner radius based on the job's position in the list
  let jobCornerStyle: any = 2; // Use 'any' to bypass type checking
  if (props.isFirst) {
    jobCornerStyle = [2, 0, 0, 2] as any; // Cast as 'any'
  } else if (props.isLast) {
    jobCornerStyle = [0, 2, 2, 0] as any; // Cast as 'any'
  }

  const actualJobConfig: Konva.RectConfig = {
    id,
    x: actualJobStart,
    y: offsetY + jobPadding,
    width: actualJobLength,
    height: actualJobHeight,
    fill: actualJobFill,
    fillEnabled: true,
    opacity: hovered ? 0.5 : 0.8,
    cornerRadius: jobCornerStyle,
    strokeWidth: 0,
  };

  const analyzedJobConfig: Konva.RectConfig = {
    id: `analyzed-${id}`,
    x: analyzedJobStart,
    y: 1 + offsetY + jobPadding + actualJobHeight,
    width: analyzedJobDuration,
    height: analyzedJobHeight,
    fill: analyzedJobFill,
    fillEnabled: true,
    opacity: hovered ? 0.4 : 0.8,
    strokeWidth: 0,
    cornerRadius: 0.5,
  };

  // Idle job rectangle with diagonal pattern fill
  const idleJobConfig: Konva.RectConfig = {
    id: `idle-${id}`,
    x: actualJobStart - idleLength,
    y: offsetY + jobPadding,
    width: idleLength,
    height: actualJobHeight,
    // Use pattern if available, fallback to solid color
    ...(patternImage
      ? {
          fillPatternImage: patternImage,
          fillPatternRepeat: 'repeat',
          fillPatternScaleX: 1,
          fillPatternScaleY: 1,
          fillEnabled: true,
        }
      : {
          fill: colors.timeline.idlePattern,
          fillEnabled: true,
        }),
    opacity: hovered ? 0.8 : 1.0,
    cornerRadius: jobCornerStyle,
  };

  function getDynamicFontSize(actualJobWidth) {
    // Minimum and maximum font sizes
    const minFontSize = 2;
    const maxFontSize = 5;

    // Define the width range for the smallest to the largest expected job widths
    const minWidthFont = 1;
    const maxWidthFont = 30;

    // Calculate the font size as a linear function of job width
    if (actualJobWidth <= minWidthFont) {
      return minFontSize;
    } else if (actualJobWidth >= maxWidthFont) {
      return maxFontSize;
    } else {
      // Linear interpolation between min and max font sizes
      return (
        minFontSize +
        (maxFontSize - minFontSize) *
          ((actualJobWidth - minWidthFont) / (maxWidthFont - minWidthFont))
      );
    }
  }

  const maxTextWidth = actualJobLength * 0.5;
  const maximumFontSize = 14;
  const initialFontSize = 10;
  const textLength = job.jobId.length;
  const estimatedCharWidth = initialFontSize * 0.6;
  const estimatedTextWidth = textLength * estimatedCharWidth;
  const scaleFactor = maxTextWidth / estimatedTextWidth;
  let fontSize = initialFontSize * scaleFactor;
  fontSize = Math.min(fontSize, maximumFontSize); // Apply the hard max font size
  // const fontSize = getDynamicFontSize(actualJobWidth);

  const jobTitleConfig: Konva.TextConfig = {
    // Left padding of 2px
    x: actualJobStart + 2,
    // Aligned to the bottom with 2px padding
    y: offsetY + jobPadding + actualJobHeight - 2,
    text: job.jobId,
    fontSize: fontSize,
    fontFamily: 'NeoSans-Regular',
    fill: getBestContrastColor(analyzedJobFill),
    width: actualJobLength,
    align: 'left',
    verticalAlign: 'bottom',
    scaleY: -1, // Mirroring the text verticallyp
    listening: false,
  };

  function selectDeadline() {}

  const eventConfig: KonvaNodeEvents = {
    onClick: onClick,
    onTap: onClick,
    onMouseEnter: e => {
      const container = e.target.getStage().container();
      container.style.cursor = 'pointer';
      setHoveredGlobal(job.jobId);
      setHovered(true);
    },

    onMouseLeave: e => {
      const container = e.target.getStage().container();
      container.style.cursor = 'default';
      setHoveredGlobal(null);
      setHovered(false);
    },
  };

  return (
    <React.Fragment key={id}>
      {/* Idle job area with diagonal pattern fill */}
      {idleLength > 0 && <Rect {...idleJobConfig} />}
      <Rect {...actualJobConfig} {...eventConfig} />
      <Rect {...analyzedJobConfig} {...eventConfig} />
      {/* <Text {...jobTitleConfig} /> */}
    </React.Fragment>
  );
};

export default React.memo(
  ActualityEventsGroupFeature,
  (prev, next) =>
    prev.job?.jobId === next.job?.jobId &&
    prev.viewJobMode === next.viewJobMode,
);
